import { Components } from '.generated/templates/Feature.Components.model';
import { Data } from '.generated/templates/Feature.Data.model';
import { Sitecore } from '.generated/templates/_.Sitecore.Override';
import { Image as SitecoreImage } from '@sitecore-jss/sitecore-jss-react';
import { classnames } from 'tailwindcss-classnames';
import LinkA11yWrapper from 'components/helpers/LinkA11yWrapper';
import { MissingDataSource } from 'components/helpers/EditingHelpText';
import RitnFooter, { RitnFooterProps } from 'components/helpers/Ritn/Footer/RitnFooter';
import NrcFooter, { NrcFooterProps } from 'components/helpers/NRC/Footer/NRCFooter';
import { useIsNRC, IsNmdpSite } from 'lib/util/site-configuration';
import NmdpSiteFooter, {
  NmdpSiteFooterProps,
} from 'components/helpers/NmdpSite/Footer/NmdpSiteFooter';
import {
  SocialLinks,
  GenericPage,
  NavigationLink,
  NavPageOrLink as pageorLink,
} from 'lib/util/site-nav';

// Re-export to support switching legacy implementation to shiny new thing
export type { SocialLinks, GenericPage, NavigationLink, pageorLink };

type navigationFooter = Sitecore.Override.ItemEx &
  Data.Navigation.Fields.NavigationFooter & { children?: NavigationLink[] };
type footerOrLink = NavigationLink | navigationFooter;

export type FooterProps = Sitecore.Override.ComponentBase & {
  fields: {
    root?: Components.Site.Fields.Footer & {
      footerLinks?: pageorLink[];
      children?: footerOrLink[] & {
        children?: NavigationLink[];
      };
      socialLinks?: SocialLinks[];
    };
  };
};

const Footer = ({ fields, rendering }: FooterProps): JSX.Element => {
  const isNrc = useIsNRC();
  if (!fields || fields?.root?.fields?.disclaimer === undefined) {
    return <MissingDataSource />;
  }
  const ritnFields = { ritnfields: fields?.root } as RitnFooterProps;
  const nrcFields = { fields } as NrcFooterProps;
  const nmdpFields = { fields, rendering } as NmdpSiteFooterProps;
  const renderSocialSection = fields?.root?.fields?.socialLinks?.length > 0 ? true : false;
  const footerContainer = classnames(
    'relative',
    'py-10',
    'px-4',
    'md:py-10',
    'overflow-hidden',
    'bg-blue-navy',
    'text-white',
    'text-center'
  );

  const SocialLinkList = fields?.root?.fields?.socialLinks?.map((item) => {
    const slink = item as SocialLinks;
    return (
      <li key={slink.id} className={classnames('px-2')}>
        <LinkA11yWrapper
          className={classnames('block', 'pt-2', 'pb-1', 'sm:pt-4', 'sm:pb-3.5', 'box-content')}
          editable={false}
          field={slink.fields.navigationItemLink}
          id={`social-links--${slink.id}`}
          rel="noreferrer noopener"
          suppressLinkText
          suppressNewTabIcon
        >
          <SitecoreImage
            field={slink.fields.icon}
            editable={false}
            className={classnames('justify-center', 'w-10', 'h-10', 'max-w-none')}
          />
        </LinkA11yWrapper>
      </li>
    );
  });

  const footerLinkList = fields?.root?.fields?.footerLinks?.map((item) => {
    const navitem = item.fields.navigationItemLink ? (item as NavigationLink) : null;
    const pageitem = !item.fields.navigationItemLink ? (item as GenericPage) : null;
    const linkfield = navitem
      ? navitem.fields.navigationItemLink
      : {
          value: {
            href: pageitem?.url,
            text:
              pageitem?.fields.navigationTitle.value ||
              pageitem?.fields.pageTitle.value ||
              pageitem?.name,
          },
        };
    return (
      <LinkA11yWrapper
        key={item.id}
        className={classnames('font-semibold', 'whitespace-nowrap', 'hover:underline', 'p-2.5')}
        editable={false}
        field={linkfield}
      />
    );
  });

  if (fields?.root?.fields?.isRitn.value) {
    return <RitnFooter {...ritnFields} />;
  }
  if (IsNmdpSite()) {
    return <NmdpSiteFooter {...nmdpFields} />;
  }
  if (isNrc) {
    return <NrcFooter {...nrcFields} />;
  }
  return (
    <div data-component="authorable/site/footer">
      <div className={footerContainer}>
        <div className={classnames('max-w-screen-2xl', 'mx-auto')}>
          {renderSocialSection && (
            <div
              className={classnames(
                'flex',
                'flex-col',
                'md:flex-row',
                'justify-center',
                'items-center'
              )}
            >
              <div className={classnames('pb-2', 'md:pr-10')}>
                {fields?.root?.fields?.socialText.value}
              </div>
              <ul className={classnames('flex', 'm-0', 'flex-wrap', 'justify-center')}>
                {SocialLinkList}
              </ul>
            </div>
          )}
          <div
            className={classnames(
              'grid',
              'py-4',
              'sm:py-0',
              'sm:flex',
              'flex-wrap',
              'justify-center'
            )}
          >
            {footerLinkList}
          </div>
          <div className={classnames('px-3.5', 'sm:pt-3.5', 'leading-1', 'text-sm', 'text-left')}>
            <p className={classnames('pb-3.5')}>{fields?.root?.fields?.disclaimer.value}</p>
            <p>
              {fields?.root?.fields?.copyright.value.replaceAll(
                '@year',
                new Date().getFullYear().toString()
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Footer;
